import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 font-sans antialiased">
      {/* Header with Animated Gradient */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-24 mb-6 drop-shadow-lg"
          />
          <motion.h1 
            className="text-5xl font-extrabold tracking-tight mb-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            className="text-xl font-light max-w-2xl mx-auto opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            How we protect your data with the same rigor we apply to cloud security
          </motion.p>
        </motion.div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <motion.section 
          className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center">
            <span className="bg-blue-100 p-2 rounded-full mr-3">🔒</span>
            Our Commitment to Security
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            At HunterFortress, we apply the same security-first principles to your personal data that we recommend for cloud infrastructure. This policy outlines what information we collect, how we use it, and the military-grade protections we employ.
          </p>
        </motion.section>

        <div className="space-y-10">
          {[
            {
              title: "Information We Collect",
              icon: "📋",
              content: (
                <div>
                  <p className="mb-4">We collect minimal data necessary to provide our services:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Contact Information:</strong> Name, email, and company details when you inquire about our services</li>
                    <li><strong>Technical Data:</strong> IP addresses, browser type, and device information for security monitoring</li>
                    <li><strong>Service Data:</strong> Cloud configuration details you provide for security audits</li>
                  </ul>
                </div>
              )
            },
            {
              title: "How We Use Your Data",
              icon: "🛡️",
              content: (
                <div>
                  <p className="mb-4">Your information is used exclusively for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Providing and improving our security services</li>
                    <li>Communicating about your security needs</li>
                    <li>Preventing fraud and unauthorized access</li>
                    <li>Complying with legal obligations (we'll notify you unless prohibited)</li>
                  </ul>
                  <p className="mt-4">We never sell your data or use it for marketing without explicit consent.</p>
                </div>
              )
            },
            {
              title: "Data Protection Measures",
              icon: "🔐",
              content: (
                <div>
                  <p className="mb-4">We implement enterprise-grade security controls:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>End-to-end encryption for all communications</li>
                    <li>Zero-trust architecture for internal systems</li>
                    <li>Regular penetration testing by third-party experts</li>
                    <li>Data minimization and automatic deletion policies</li>
                  </ul>
                  <p className="mt-4">All cloud security assessments are conducted in isolated environments with strict access controls.</p>
                </div>
              )
            },
            {
              title: "Your Rights",
              icon: "⚖️",
              content: (
                <div>
                  <p className="mb-4">You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access, correct, or delete your personal data</li>
                    <li>Object to processing or request restrictions</li>
                    <li>Receive your data in a portable format</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                  <p className="mt-4">To exercise these rights, contact us at <a href="mailto:security@hunterfortress.com" className="text-blue-600 hover:underline">privacy@hunterfortress.com</a>.</p>
                </div>
              )
            },
            {
              title: "Policy Updates",
              icon: "🔄",
              content: (
                <div>
                  <p>We may update this policy to reflect changes in regulations or our services. Significant changes will be communicated via email or through our website. The last update was made on August 9, 2025.</p>
                </div>
              )
            }
          ].map((section, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-blue-800 mb-4 flex items-center">
                <span className="bg-blue-100 p-2 rounded-full mr-3">{section.icon}</span>
                {section.title}
              </h3>
              <div className="text-gray-700 leading-relaxed">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-blue-300 transition font-semibold">Home</Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">Contact</Link>
            <Link to="/storepal" className="hover:text-blue-300 transition font-semibold">StorePal</Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">Terms</Link>
			<Link to="/privacy" className="hover:text-blue-300 transition">Privacy</Link>
			<Link to="/blog" className="hover:text-blue-300 transition">Blog</Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;